/**
 * FE-PR-20 — pure sign-in view-model helpers (no React, no side effects).
 */
import type { TFunction } from 'i18next';
import type { ZodIssue } from 'zod';
import type { RequestOtpResponse } from '../authApi.js';
import { classifySignInIdentifier } from '../validation.js';

export function maskEmailForPending(identifier: string): string | null {
  const classified = classifySignInIdentifier(identifier);
  if (classified?.channel !== 'email') {
    return null;
  }
  const at = classified.identifier.indexOf('@');
  if (at <= 0) {
    return classified.identifier;
  }
  return `${classified.identifier[0]}***${classified.identifier.slice(at)}`;
}

export function buildConsentFieldErrors(issues: readonly ZodIssue[]): Record<string, string> {
  const next: Record<string, string> = {};
  for (const issue of issues) {
    let key = String(issue.path.join('.') || 'consents');
    // otpConsentsSchema / registrationConsentsSchema parse the consents object
    // directly — Zod paths are `termsAccepted`, not `consents.termsAccepted`.
    // FormErrorSummary + RegistrationConsentsField expect the consents.* keys.
    if (key === 'termsAccepted' || key === 'privacyAccepted') {
      key = `consents.${key}`;
    }
    if (next[key] === undefined) {
      next[key] = issue.message;
    }
  }
  return next;
}

export function resolveSignInChannelHint(input: {
  readonly t: TFunction<'customer'>;
  readonly authMethod: 'otp' | 'password';
  readonly challenge: RequestOtpResponse | null;
  readonly identifier: string;
  readonly resolvedHelperText: string;
  readonly passwordHintKey?: 'auth.passwordHint' | 'auth.passwordHintWithPhone';
}): string {
  if (input.authMethod === 'password') {
    return input.t(input.passwordHintKey ?? 'auth.passwordHint');
  }
  if (input.challenge !== null) {
    return input.challenge.channel === 'email'
      ? input.t('auth.codeSentEmail')
      : input.t('auth.codeSentPhone');
  }
  const classified = classifySignInIdentifier(input.identifier);
  if (classified === null) {
    return input.resolvedHelperText;
  }
  return classified.channel === 'email'
    ? input.t('auth.otpWillSendEmail')
    : input.t('auth.otpWillSendSms');
}
